import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { PicklistValue } from '@/api/client';

/**
 * Dropdown of checkboxes over string values (UDF picklists, RMM statuses…).
 * The numeric-picklist sibling is `MultiSelect`. Highlights when anything is selected.
 */
export function StringMultiSelect({
  label, options, selected, onChange,
}: {
  label: string;
  options: PicklistValue[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const summary = useMemo(() => {
    if (selected.length === 0) return label;
    if (selected.length === 1) return `${label}: ${options.find((o) => o.value === selected[0])?.label ?? selected[0]}`;
    return `${label}: ${selected.length}`;
  }, [label, options, selected]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={selected.length > 0 ? 'secondary' : 'outline'} size="sm">{summary}</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-auto">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {options.map((o) => (
          <DropdownMenuCheckboxItem
            key={o.value}
            checked={selected.includes(o.value)}
            onCheckedChange={(c) => onChange(c ? [...selected, o.value] : selected.filter((x) => x !== o.value))}
            onSelect={(e) => e.preventDefault()}
          >
            {o.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
